(() => {
  const STATUS_URL = '/apps/aow-trade';
  const FACTOR = 0.7;
  let isTrade = false;
  let observerQueued = false;
  let tradeObserver;

  const money = new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  const tradeCents = (value) => Math.round(Number(value || 0) * FACTOR);
  const formatCents = (value) => money.format(Number(value || 0) / 100);

  function applyProductPrice(node) {
    if (!node || !isTrade) return;

    const price = Number(node.dataset.aowRetailPriceCents || 0);
    const min = Number(node.dataset.aowRetailMinCents || price);
    const max = Number(node.dataset.aowRetailMaxCents || price);
    const unit = Number(node.dataset.aowRetailUnitCents || 0);
    const useVariant = node.dataset.aowUseVariant === 'true';
    const varies = node.dataset.aowPriceVaries === 'true';
    const volumeRange = node.dataset.aowVolumeRange === 'true';

    const regular = node.querySelector('.price__regular .price-item--regular');
    if (regular) {
      if (!useVariant && volumeRange && max > min) {
        regular.textContent = formatCents(tradeCents(min)) + ' – ' + formatCents(tradeCents(max));
      } else if (!useVariant && varies) {
        regular.textContent = 'From ' + formatCents(tradeCents(min));
      } else {
        regular.textContent = formatCents(tradeCents(price));
      }
    }

    const label = node.querySelector('[data-aow-trade-price-label]');
    if (label) label.hidden = false;

    const unitNode = node.querySelector('.unit-price .price-item');
    if (unitNode && unit > 0) {
      unitNode.textContent = formatCents(tradeCents(unit));
    }

    node.classList.add('aow-trade-price-active');
    node.classList.remove('price--on-sale', 'price--show-badge', 'volume-pricing--sale-badge');
  }

  function applyCustomProductCard(card) {
    if (!card || !isTrade) return;
    const price = Number(card.dataset.aowRetailPriceCents || 0);
    const unit = Number(card.dataset.aowRetailUnitCents || 0);
    const varies = card.dataset.aowPriceVaries === 'true';

    const label = card.querySelector('[data-aow-custom-trade-label]');
    if (label) label.hidden = false;

    const priceNode = card.querySelector('[data-aow-custom-price]');
    if (priceNode && price > 0) {
      priceNode.textContent = (varies ? 'From ' : '') + formatCents(tradeCents(price));
    }

    const unitNode = card.querySelector('[data-aow-custom-unit]');
    if (unitNode && unit > 0) {
      const labelText = unitNode.dataset.aowUnitLabel || 'unit';
      unitNode.textContent = '(' + formatCents(tradeCents(unit)) + ' per ' + labelText + ')';
    }

    card.classList.add('aow-trade-price-active');
  }

  function applyVariantPrice(node) {
    if (!node || !isTrade) return;
    const price = Number(node.dataset.aowRetailPriceCents || 0);
    const unit = Number(node.dataset.aowRetailUnitCents || 0);

    const priceNode = node.querySelector('[data-aow-variant-main-price]');
    if (priceNode && price > 0) {
      priceNode.textContent = formatCents(tradeCents(price));
    }

    const unitNode = node.querySelector('[data-aow-variant-unit-price]');
    if (unitNode && unit > 0) {
      const unitLabel = unitNode.dataset.aowUnitLabel || 'unit';
      unitNode.textContent = formatCents(tradeCents(unit)) + ' per ' + unitLabel;
    }

    node.classList.add('aow-trade-price-active');
  }

  function applyQuickAdd(root) {
    if (!root || !isTrade) return;

    root.querySelectorAll?.('[data-aow-quick-add-variant-price]').forEach((node) => {
      const price = Number(node.dataset.retailPriceCents || 0);
      const unit = Number(node.dataset.retailUnitCents || 0);
      const unitLabel = node.dataset.unitLabel || '';

      const main = node.querySelector('[data-aow-quick-add-variant-main]');
      if (main && price > 0) main.textContent = formatCents(tradeCents(price));

      const unitNode = node.querySelector('[data-aow-quick-add-variant-unit]');
      if (unitNode && unit > 0) {
        unitNode.textContent = formatCents(tradeCents(unit)) + (unitLabel ? ' per ' + unitLabel : '');
      }
    });

    root.querySelectorAll?.('[data-aow-quick-add-trade-label]').forEach((label) => {
      label.hidden = false;
    });

    root.querySelectorAll?.('[data-aow-variant].is-selected').forEach((button) => {
      const priceTarget = button.closest('[data-aow-quick-add-overlay]')?.querySelector('[data-aow-selected-price]');
      const unitTarget = button.closest('[data-aow-quick-add-overlay]')?.querySelector('[data-aow-selected-unit-price]');
      const price = Number(button.dataset.variantPriceCents || 0);
      const unit = Number(button.dataset.unitPriceCents || 0);
      const unitLabel = button.dataset.unitLabel || '';

      if (priceTarget && price > 0) priceTarget.textContent = formatCents(tradeCents(price));
      if (unitTarget && unit > 0) {
        unitTarget.textContent = formatCents(tradeCents(unit)) + (unitLabel ? ' per ' + unitLabel : '');
      }
    });
  }

  function applyCartItem(row) {
    if (!row || !isTrade) return;

    const unitBase = Number(row.dataset.aowUnitPriceCents || 0);
    const lineBase = Number(row.dataset.aowLinePriceCents || 0);
    const stems = Number(row.dataset.aowStemCount || 0);

    row.querySelectorAll('[data-aow-trade-cart-badge]').forEach((badge) => {
      badge.hidden = false;
    });

    if (stems > 0 && unitBase > 0) {
      const perUnit = Math.round(tradeCents(unitBase) / stems);
      row.querySelectorAll('[data-aow-cart-unit-display]').forEach((el) => {
        el.textContent = formatCents(perUnit);
      });
    }

    row.querySelectorAll('.cart-item__price-wrapper').forEach((wrapper) => {
      let display = wrapper.querySelector('.aow-trade-cart-line-price');
      if (!display) {
        display = document.createElement('span');
        display.className = 'price price--end aow-trade-cart-line-price';
        wrapper.appendChild(display);
      }
      display.textContent = formatCents(tradeCents(lineBase));
    });

    row.querySelectorAll('[data-aow-cart-unit-wrapper]').forEach((wrapper) => {
      let display = wrapper.querySelector('.aow-trade-cart-unit-price');
      if (!display) {
        display = document.createElement('strong');
        display.className = 'product-option aow-trade-cart-unit-price';
        wrapper.appendChild(display);
      }
      display.textContent = formatCents(tradeCents(unitBase));
    });

    row.querySelectorAll('.cart-item__mobile-total strong').forEach((el) => {
      el.textContent = formatCents(tradeCents(lineBase));
    });
  }

  function applyCartTotal(node) {
    if (!node || !isTrade) return;
    const base = Number(node.dataset.aowCartTotalCents || 0);
    if (base > 0) node.textContent = formatCents(tradeCents(base));
  }

  function applyTradePricing(root = document) {
    if (!isTrade) return;
    tradeObserver?.disconnect();
    root.querySelectorAll?.('[data-aow-product-price]').forEach(applyProductPrice);
    root.querySelectorAll?.('[data-aow-custom-product-card]').forEach(applyCustomProductCard);
    root.querySelectorAll?.('[data-aow-variant-price]').forEach(applyVariantPrice);
    applyQuickAdd(root);
    root.querySelectorAll?.('[data-aow-cart-item]').forEach(applyCartItem);
    root.querySelectorAll?.('[data-aow-cart-total]').forEach(applyCartTotal);
    tradeObserver?.observe(document.documentElement, { childList: true, subtree: true });
    window.AOWVat?.refresh();
  }

  function setTradeState(trade) {
    isTrade = Boolean(trade);
    document.documentElement.classList.toggle('aow-trade-customer', isTrade);
    document.documentElement.classList.add('aow-trade-status-ready');
    if (isTrade) applyTradePricing(document);
    document.dispatchEvent(new CustomEvent('aow:trade-pricing-ready', {
      detail: { trade: isTrade },
    }));
  }

  function storefrontTradeStatus() {
    const configNode = document.getElementById('aow-vat-config');
    if (!configNode) return null;

    try {
      const storefrontConfig = JSON.parse(configNode.textContent);
      return typeof storefrontConfig.tradeCustomer === 'boolean'
        ? storefrontConfig.tradeCustomer
        : null;
    } catch (_) {
      return null;
    }
  }

  async function checkTradeStatus() {
    const storefrontTrade = storefrontTradeStatus();
    if (storefrontTrade !== null) {
      setTradeState(storefrontTrade);
      return;
    }
    try {
      const response = await fetch(STATUS_URL, {
        method: 'GET',
        credentials: 'same-origin',
        cache: 'no-store',
        headers: { Accept: 'application/json' },
      });
      if (!response.ok) {
        setTradeState(false);
        return;
      }
      const data = await response.json();
      setTradeState(data && data.trade === true);
    } catch (_) {
      setTradeState(false);
    }
  }

  function startObserver() {
    tradeObserver = new MutationObserver(() => {
      if (!isTrade || observerQueued) return;
      observerQueued = true;
      requestAnimationFrame(() => {
        observerQueued = false;
        applyTradePricing(document);
      });
    });
    tradeObserver.observe(document.documentElement, { childList: true, subtree: true });
  }

  window.AOWTradePricing = {
    refresh: checkTradeStatus,
    apply: (root = document) => applyTradePricing(root),
    isTrade: () => isTrade,
    formatTradeCents: (cents) => formatCents(tradeCents(cents)),
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      startObserver();
      checkTradeStatus();
    }, { once: true });
  } else {
    startObserver();
    checkTradeStatus();
  }

  document.addEventListener('shopify:section:load', () => applyTradePricing(document));
})();